<div class="tc-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'testCases.pageTitle' | translate" [subtitle]="'testCases.pageSubtitle' | translate">
    <ng-container right>
      @if (statItems().length) {
        <app-stat-group
          class="tc-header-stats"
          role="group"
          [items]="statItems()"
          [attr.aria-label]="statsAriaLabel()"
        />
      }
      @if (!isReadOnly()) {
        <button mat-stroked-button type="button" class="tc-import-btn" (click)="openImportModal()">
          <mat-icon aria-hidden="true">upload</mat-icon>
          {{ 'testCases.import.buttonLabel' | translate }}
        </button>
        <button mat-flat-button color="primary" type="button" class="tc-create-btn" [routerLink]="['new']">
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'testCases.addButton' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="tc-toolbar">
    <app-search-bar
      [placeholder]="'testCases.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <div class="tc-toolbar-spacer"></div>
    <app-test-case-filter-bar
      [statusOptions]="statusOptions()"
      [executionOptions]="executionOptions()"
      [priorityOptions]="priorityOptions()"
      [authorOptions]="authorOptions()"
      [selectedStatus]="selectedStatus()"
      [selectedExecution]="selectedExecution()"
      [selectedPriority]="selectedPriority()"
      [selectedAuthor]="selectedAuthor()"
      [tags]="tagOptions()"
      [tagFilter]="tagFilter()"
      [hasActiveFilters]="hasActiveFilters()"
      [savedViews]="savedViewsOptions()"
      [activeSavedView]="activeSavedViewName()"
      [projectId]="projectId"
      (filterChange)="onFilterChange($event)"
      (tagChange)="onTagFilterChange($event)"
      (resetFilters)="onResetFilters()"
      (savedViewSelected)="onSavedViewSelected($event)"
      (savedViewSaved)="onSavedViewSaved($event)"
      (savedViewDeleted)="onSavedViewDeleted($event)"
      (savedViewDeleteFailed)="onSavedViewDeleteFailed()"
    />
  </div>

  @if (deleteError()) {
    <p class="tc-hint tc-hint--error" role="alert">{{ 'testCases.deleteError' | translate }}</p>
  }

  @if (savedViewsError()) {
    <p class="tc-hint tc-hint--error" role="alert">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <div class="tc-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadUnavailable()) {
    <div class="tc-hint tc-hint--error tc-hint--unavailable" role="alert" data-testid="error-unavailable">
      <p class="tc-hint__text">{{ 'testCases.loadUnavailable' | translate }}</p>
      <button mat-stroked-button type="button" data-testid="retry-load" (click)="retryLoad()">
        <mat-icon aria-hidden="true">refresh</mat-icon>
        {{ 'common.retry' | translate }}
      </button>
    </div>
  } @else if (loadError()) {
    <p class="tc-hint tc-hint--error" role="alert">{{ 'testCases.loadError' | translate }}</p>
  } @else if (rows().length === 0) {
    <p class="tc-hint">{{ 'testCases.empty' | translate }}</p>
  } @else {
    <div appListFocusScroll class="tc-card">
      <div class="tc-table-header" role="row">
        <div class="tc-col tc-col--id" role="columnheader" [attr.aria-sort]="sortAria('key')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('key')">
            {{ 'testCases.table.id' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'key'"
              aria-hidden="true"
            >
              {{ sortIcon('key') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--title" role="columnheader" [attr.aria-sort]="sortAria('title')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('title')">
            {{ 'testCases.table.title' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'title'"
              aria-hidden="true"
            >
              {{ sortIcon('title') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--source" role="columnheader" [attr.aria-sort]="sortAria('source')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('source')">
            {{ 'testCases.table.source' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'source'"
              aria-hidden="true"
            >
              {{ sortIcon('source') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--steps" role="columnheader" [attr.aria-sort]="sortAria('stepCount')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('stepCount')">
            {{ 'testCases.table.steps' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'stepCount'"
              aria-hidden="true"
            >
              {{ sortIcon('stepCount') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--priority" role="columnheader" [attr.aria-sort]="sortAria('priority')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('priority')">
            {{ 'testCases.table.priority' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'priority'"
              aria-hidden="true"
            >
              {{ sortIcon('priority') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--execution" role="columnheader" [attr.aria-sort]="sortAria('execution')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('execution')">
            {{ 'testCases.table.execution' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'execution'"
              aria-hidden="true"
            >
              {{ sortIcon('execution') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--status" role="columnheader" [attr.aria-sort]="sortAria('status')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('status')">
            {{ 'testCases.table.status' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'status'"
              aria-hidden="true"
            >
              {{ sortIcon('status') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--author" role="columnheader" [attr.aria-sort]="sortAria('authorName')">
          <button type="button" class="tc-sort-btn" (click)="sortBy('authorName')">
            {{ 'testCases.table.author' | translate }}
            <mat-icon
              class="tc-sort-btn__icon"
              [class.tc-sort-btn__icon--active]="tableSort().col === 'authorName'"
              aria-hidden="true"
            >
              {{ sortIcon('authorName') }}
            </mat-icon>
          </button>
        </div>
        <div class="tc-col tc-col--actions" role="columnheader">
          {{ 'testCases.table.actions' | translate }}
        </div>
      </div>

      @if (!hasResults()) {
        <p class="tc-empty-filtered">{{ 'testCases.emptyFiltered' | translate }}</p>
      }

      @for (row of pagedRows(); track row.id; let last = $last) {
        <div
          class="tc-row"
          [class.tc-row--last]="last"
          role="link"
          tabindex="0"
          [attr.aria-label]="row.title"
          (click)="onRowClick(row)"
          (keydown.enter)="onRowKeydown($event, row)"
        >
          <div class="tc-col tc-col--id">
            <app-code-id-chip [id]="row.key" />
          </div>
          <div class="tc-col tc-col--title">
            <span class="tc-title">{{ row.title }}</span>
            @if (row.description) {
              <span class="tc-subtitle">{{ row.description }}</span>
            }
          </div>
          <div class="tc-col tc-col--source">
            @if (row.source) {
              <span class="tc-source" [attr.title]="'testCases.table.sourceTitle' | translate: { source: row.source }">
                <mat-icon class="tc-source__icon" aria-hidden="true">trending_up</mat-icon>
                <code class="tc-source__ref">{{ row.source }}</code>
              </span>
            } @else {
              <span class="tc-none">—</span>
            }
          </div>
          <div class="tc-col tc-col--steps">
            <span class="tc-steps__count">{{ row.stepCount }}</span>
            <span class="tc-steps__unit">{{ 'testCases.table.stepsUnit' | translate }}</span>
          </div>
          <div class="tc-col tc-col--priority">
            <app-priority-pill [level]="row.priorityLevel" [label]="row.priorityLabel" />
          </div>
          <div class="tc-col tc-col--execution">
            <app-tc-status-pill
              [kind]="row.executionKind"
              [label]="row.executionLabel"
              [title]="row.executionTooltip"
            />
          </div>
          <div class="tc-col tc-col--status">
            <app-lifecycle-pill [kind]="row.lifecycleKind" [label]="row.statusLabel" />
          </div>
          <div class="tc-col tc-col--author">
            @if (row.authorName) {
              <app-avatar [name]="row.authorName" [size]="28" aria-hidden="true" />
              <div class="tc-author__text">
                <span class="tc-author__name">{{ row.authorName }}</span>
                <span
                  class="tc-author__meta"
                  [attr.title]="row.lastExecutedAt ? (row.lastExecutedAt | date: lastRunTimePattern) : null"
                >
                  {{ row.lastRunLabel }}
                </span>
              </div>
            } @else {
              <span class="tc-none" [attr.aria-label]="'testCases.table.noAuthor' | translate">—</span>
            }
          </div>
          <div class="tc-col tc-col--actions">
            @if (!isReadOnly()) {
              <button
                type="button"
                class="tc-action-btn tc-action-btn--danger"
                [attr.aria-label]="'testCases.delete' | translate"
                (click)="$event.stopPropagation(); onDeleteRequest(row)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>

    <div class="tc-footer">
      <span class="tc-footer__range">
        {{ 'testCases.footer.rangeLabel' | translate: { start: rangeStart(), end: rangeEnd(), total: total() } }}
      </span>
      <div class="tc-footer__pager">
        <label class="tc-footer__label" for="tc-page-size">{{ 'testCases.footer.rowsPerPage' | translate }}</label>
        <select
          id="tc-page-size"
          class="tc-footer__select"
          (change)="onPageSizeChange(+pageSizeSelect.value)"
          #pageSizeSelect
          [attr.aria-label]="'testCases.footer.rowsPerPageAriaLabel' | translate"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
          }
        </select>
        @if (pageCount() > 1) {
          <div class="tc-footer__nav">
            <button
              type="button"
              class="tc-action-btn"
              [disabled]="pageIndex() === 0"
              [attr.aria-label]="'testCases.footer.previousPage' | translate"
              (click)="onPageChange(pageIndex() - 1)"
            >
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
            </button>
            <button
              type="button"
              class="tc-action-btn"
              [disabled]="pageIndex() >= pageCount() - 1"
              [attr.aria-label]="'testCases.footer.nextPage' | translate"
              (click)="onPageChange(pageIndex() + 1)"
            >
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </div>
        }
      </div>
    </div>
  }
</div>

<app-confirm-dialog
  [visible]="showDeleteModal()"
  [title]="'testCases.deleteTitle' | translate"
  [message]="deleteMessage()"
  [confirmLabel]="'testCases.deleteConfirm' | translate"
  [cancelLabel]="'testCases.deleteCancel' | translate"
  [confirmDisabled]="deleteUsageLoading()"
  (confirmed)="onConfirmDelete()"
  (cancelled)="onCancelDelete()"
>
  @if (deleteUsagePlans().length; as count) {
    <p class="tc-delete-usage">
      {{
        (count === 1 ? 'testCases.deleteUsagePlansOne' : 'testCases.deleteUsagePlansOther')
          | translate: { count, names: deleteUsageNames() }
      }}
    </p>
  }
</app-confirm-dialog>

<app-test-case-import-modal [(visible)]="showImportModal" [projectId]="projectId" (imported)="onImported()" />
